import { recomputeMeasurementValue } from "@constructionautomation/backend/convex/lib/measurementQuantities";
import { mapPolygonPoints, type PolygonGeometry } from "@constructionautomation/backend/convex/lib/measurementGeometry";
import { calibratePageScale } from "@constructionautomation/backend/convex/lib/drawingScale";
import { z } from "zod";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  MEASUREMENT_TOOL_IDS,
  MEASUREMENT_TYPES,
} from "@/lib/measurement-domain";

/**
 * Represents a 2D point in pixel coordinates.
 */
export interface Point {
  x: number;
  y: number;
}

function resolvePageAspectRatio(pageAspectRatio: number | undefined): number {
  return Number.isFinite(pageAspectRatio) && (pageAspectRatio as number) > 0
    ? (pageAspectRatio as number)
    : 1;
}

/**
 * Result of a scale calibration operation.
 * @property pixelsPerFoot - Number of pixels per real-world foot
 * @property feetPerPixel - Number of feet per pixel (inverse of pixelsPerFoot)
 */
export interface CalibrationResult {
  pixelsPerFoot: number;
  feetPerPixel: number;
}

/**
 * A preset architectural scale for drawings.
 * @property label - Human-readable label (e.g., "1/4\" = 1'")
 * @property ratio - The scale ratio string
 * @property inchesPerFoot - Inches on paper per real foot
 */
export interface PresetScale {
  label: string;
  ratio: string;
  inchesPerFoot: number;
}

/**
 * Common architectural drawing scales.
 */
export const ARCHITECTURAL_PRESETS: PresetScale[] = [
  { label: "1/8\" = 1'", ratio: "1/8\"=1'", inchesPerFoot: 0.125 },
  { label: "1/4\" = 1'", ratio: "1/4\"=1'", inchesPerFoot: 0.25 },
  { label: "1/2\" = 1'", ratio: "1/2\"=1'", inchesPerFoot: 0.5 },
  { label: "1\" = 1'", ratio: "1\"=1'", inchesPerFoot: 1 },
];

/**
 * Standard paper size presets for PDF calibration.
 * Dimensions are in PDF points (72 points = 1 inch).
 */
export interface PageSizePreset {
  label: string;
  widthInches: number;
  heightInches: number;
  widthPoints: number;
  heightPoints: number;
}

/**
 * Common architectural/engineering paper sizes.
 */
export const PAGE_SIZE_PRESETS: PageSizePreset[] = [
  { label: "Letter (8.5\" × 11\")", widthInches: 8.5, heightInches: 11, widthPoints: 612, heightPoints: 792 },
  { label: "Ledger (17\" × 11\")", widthInches: 17, heightInches: 11, widthPoints: 1224, heightPoints: 792 },
  { label: "Tabloid (11\" × 17\")", widthInches: 11, heightInches: 17, widthPoints: 792, heightPoints: 1224 },
  { label: "Legal (8.5\" × 14\")", widthInches: 8.5, heightInches: 14, widthPoints: 612, heightPoints: 1008 },
  { label: "A4 (210 × 297 mm)", widthInches: 8.27, heightInches: 11.69, widthPoints: 595, heightPoints: 842 },
  { label: "A3 (297 × 420 mm)", widthInches: 11.69, heightInches: 16.54, widthPoints: 842, heightPoints: 1191 },
];

/**
 * Detect a page size preset from PDF point dimensions.
 * @param widthPoints - Page width in PDF points
 * @param heightPoints - Page height in PDF points
 * @param tolerance - Matching tolerance in points (default 2)
 * @returns The matching PageSizePreset or null if no match
 */
export function detectPageSize(
  widthPoints: number,
  heightPoints: number,
  tolerance: number = 2
): PageSizePreset | null {
  for (const preset of PAGE_SIZE_PRESETS) {
    const widthMatch =
      Math.abs(widthPoints - preset.widthPoints) <= tolerance ||
      Math.abs(widthPoints - preset.heightPoints) <= tolerance;
    const heightMatch =
      Math.abs(heightPoints - preset.heightPoints) <= tolerance ||
      Math.abs(heightPoints - preset.widthPoints) <= tolerance;
    if (widthMatch && heightMatch) {
      const isSwapped =
        Math.abs(widthPoints - preset.heightPoints) <= tolerance &&
        Math.abs(heightPoints - preset.widthPoints) <= tolerance;
      if (isSwapped) {
        return {
          ...preset,
          widthPoints: preset.heightPoints,
          heightPoints: preset.widthPoints,
          widthInches: preset.heightInches,
          heightInches: preset.widthInches,
        };
      }
      return preset;
    }
  }
  return null;
}

/**
 * Calculate the Euclidean distance between two points in pixels.
 * @param p1 - First point
 * @param p2 - Second point
 * @returns Distance in pixels
 */
export function calculatePixelDistance(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate Euclidean distance in normalized page space using aspect-corrected Y.
 * @param p1 - First point
 * @param p2 - Second point
 * @param pageAspectRatio - Page height / width ratio
 * @returns Distance in width-normalized page units
 */
export function calculatePageDistance(
  p1: Point,
  p2: Point,
  pageAspectRatio: number = 1
): number {
  const dx = p2.x - p1.x;
  const dy = (p2.y - p1.y) * resolvePageAspectRatio(pageAspectRatio);
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calibrate scale from two pixel points and a known real-world distance.
 * @param p1 - First point in pixel coordinates
 * @param p2 - Second point in pixel coordinates
 * @param realWorldDistanceFeet - Known distance between points in feet
 * @returns Calibration result with pixelsPerFoot and feetPerPixel
 * @throws Error if pixel distance is zero or real-world distance is non-positive
 */
export function calibrateFromPoints(
  p1: Point,
  p2: Point,
  realWorldDistanceFeet: number,
  pageAspectRatio: number = 1
): CalibrationResult {
  const pixelDistance = calculatePageDistance(p1, p2, pageAspectRatio);
  if (pixelDistance <= 0) {
    throw new Error("Invalid calibration: zero pixel distance");
  }
  if (realWorldDistanceFeet <= 0) {
    throw new Error("Invalid calibration: non-positive real-world distance");
  }
  const pixelsPerFoot = pixelDistance / realWorldDistanceFeet;
  const feetPerPixel = realWorldDistanceFeet / pixelDistance;
  return { pixelsPerFoot, feetPerPixel };
}

/**
 * Calibrate scale from a known page size and architectural scale preset.
 * Returns pixelsPerFoot in NORMALIZED coordinate space (where 1.0 = full viewport width),
 * to be consistent with manual calibration which uses normalized coordinates.
 * @param pageWidthInches - Physical page width in inches
 * @param preset - Architectural preset scale
 * @returns Calibration result with pixelsPerFoot and feetPerPixel
 */
export function calibrateFromPageSizeAndScale(
  pageWidthInches: number,
  preset: PresetScale
): CalibrationResult {
  return calibratePageScale(pageWidthInches, preset.inchesPerFoot);
}

/**
 * Convert pixels to feet using a calibration scale.
 * @param pixels - Distance in pixels
 * @param pixelsPerFoot - Scale factor (pixels per foot)
 * @returns Distance in feet
 */
export function pixelsToFeet(pixels: number, pixelsPerFoot: number): number {
  return pixels / pixelsPerFoot;
}

/**
 * Convert feet to pixels using a calibration scale.
 * @param feet - Distance in feet
 * @param pixelsPerFoot - Scale factor (pixels per foot)
 * @returns Distance in pixels
 */
export function feetToPixels(feet: number, pixelsPerFoot: number): number {
  return feet * pixelsPerFoot;
}

export type MeasurementTool =
  (typeof MEASUREMENT_TOOL_IDS)[keyof typeof MEASUREMENT_TOOL_IDS];
export type MeasurementType =
  (typeof MEASUREMENT_TYPES)[keyof typeof MEASUREMENT_TYPES];
export type MeasurementGeometry =
  | {
      type: typeof MEASUREMENT_GEOMETRY_TYPES.rect;
      x: number;
      y: number;
      width: number;
      height: number;
    }
  | PolygonGeometry;

type MeasurementFieldFormat =
  | {
      mode: "architectural";
      fractionDenominator: number;
    }
  | {
      mode: "decimal";
      decimals: number;
      step: number;
    };

export const MEASUREMENT_FORMAT_CONFIG: Record<
  MeasurementType | "height",
  MeasurementFieldFormat
> = {
  [MEASUREMENT_TYPES.LF]: {
    mode: "architectural",
    fractionDenominator: 16,
  },
  [MEASUREMENT_TYPES.SQFT]: {
    mode: "decimal",
    decimals: 0,
    step: 1,
  },
  [MEASUREMENT_TYPES.CF]: {
    mode: "decimal",
    decimals: 0,
    step: 1,
  },
  height: {
    mode: "architectural",
    fractionDenominator: 16,
  },
};

function gcd(a: number, b: number): number {
  let left = Math.abs(a);
  let right = Math.abs(b);

  while (right !== 0) {
    const remainder = left % right;
    left = right;
    right = remainder;
  }

  return left || 1;
}

function roundToStep(value: number, step: number): number {
  if (step <= 0) {
    return value;
  }

  return Math.round(value / step) * step;
}

function parseFraction(input: string): number | null {
  const match = input.trim().match(/^(\d+)\s*\/\s*(\d+)$/);
  if (!match) {
    return null;
  }

  const numerator = Number.parseInt(match[1], 10);
  const denominator = Number.parseInt(match[2], 10);
  if (denominator <= 0) {
    return null;
  }

  return numerator / denominator;
}

function parseMixedNumber(input: string): number | null {
  const normalized = input.trim().replace(/\s+/g, " ");
  if (normalized.length === 0) {
    return null;
  }

  if (/^\d+(?:\.\d+)?$/.test(normalized)) {
    return Number.parseFloat(normalized);
  }

  const fractionValue = parseFraction(normalized);
  if (fractionValue !== null) {
    return fractionValue;
  }

  const mixedMatch = normalized.match(/^(\d+)\s+(\d+\s*\/\s*\d+)$/);
  if (!mixedMatch) {
    return null;
  }

  const whole = Number.parseInt(mixedMatch[1], 10);
  const fraction = parseFraction(mixedMatch[2]);
  if (fraction === null) {
    return null;
  }

  return whole + fraction;
}

/**
 * Format a decimal-feet measurement as architectural feet/inches text.
 *
 * Values are interpreted as decimal feet and rounded to the nearest fractional
 * inch using `Math.round(feet * 12 * fractionDenominator)`.
 *
 * Output forms:
 * - `X'-Y"` for whole inches
 * - `X'-Y N/D"` for fractional inches
 *
 * Edge cases:
 * - Non-finite values return `0'-0"`
 * - Rounded `12"` rolls over into the next foot
 *
 * Examples:
 * - `formatFeetInches(10.5)` -> `10'-6"`
 * - `formatFeetInches(10 + 6.25 / 12, 16)` -> `10'-6 1/4"`
 *
 * @param feet Distance in decimal feet.
 * @param fractionDenominator Fractional-inch precision denominator. Defaults to
 *   the configured architectural display precision for linear measurements.
 * @returns Architectural notation string.
 */
export function formatFeetInches(
  feet: number,
  fractionDenominator: number = MEASUREMENT_FORMAT_CONFIG.LF.mode === "architectural"
    ? MEASUREMENT_FORMAT_CONFIG.LF.fractionDenominator
    : 16
): string {
  if (!Number.isFinite(feet)) {
    return `0'-0"`;
  }

  const denominator = Math.max(1, fractionDenominator);
  const totalFractionalInches = Math.round(feet * 12 * denominator);
  const fractionalInchesPerFoot = 12 * denominator;
  const wholeFeet = Math.floor(totalFractionalInches / fractionalInchesPerFoot);
  const remainingFractionalInches = totalFractionalInches - wholeFeet * fractionalInchesPerFoot;
  let wholeInches = Math.floor(remainingFractionalInches / denominator);
  let fractionalInches = remainingFractionalInches - wholeInches * denominator;

  if (fractionalInches === denominator) {
    wholeInches += 1;
    fractionalInches = 0;
  }

  if (wholeInches === 12) {
    return `${wholeFeet + 1}'-0"`;
  }

  if (fractionalInches === 0) {
    return `${wholeFeet}'-${wholeInches}"`;
  }

  const divisor = gcd(fractionalInches, denominator);
  const numerator = fractionalInches / divisor;
  const reducedDenominator = denominator / divisor;
  return `${wholeFeet}'-${wholeInches} ${numerator}/${reducedDenominator}"`;
}

/**
 * Parse architectural distance text into decimal feet.
 *
 * Accepted inputs include:
 * - `10'-6"`
 * - `10'-6 1/4"`
 * - `10'`
 * - `120"`
 * - `10.5`
 *
 * The parser accepts straight and typographic quote characters and returns
 * `null` for invalid input, including inch values greater than or equal to 12
 * in feet/inches mode.
 *
 * Examples:
 * - `parseFeetInches("10'-6")` -> `10.5`
 * - `parseFeetInches("10'-6 1/4\"")` -> `10.520833...`
 * - `parseFeetInches("120\"")` -> `10`
 *
 * @param input Architectural or decimal distance string.
 * @returns Distance in decimal feet, or `null` if parsing fails.
 */
export function parseFeetInches(input: string): number | null {
  const trimmed = input
    .trim()
    .replace(/[′’]/g, "'")
    .replace(/[″”]/g, '"')
    .replace(/\s+/g, " ");

  if (trimmed.length === 0) {
    return null;
  }

  if (trimmed.includes("'")) {
    const [feetPart, ...restParts] = trimmed.split("'");
    if (restParts.length > 1) {
      return null;
    }

    if (!/^\d+(?:\.\d+)?$/.test(feetPart.trim())) {
      return null;
    }

    const feet = Number.parseFloat(feetPart.trim());
    let rest = (restParts[0] ?? "").trim();
    if (rest.length === 0) {
      return feet;
    }

    rest = rest.replace(/^-\s*/, "").replace(/"/g, "").trim();
    const inches = parseMixedNumber(rest);
    if (inches === null || inches >= 12) {
      return null;
    }

    return feet + inches / 12;
  }

  if (trimmed.includes('"')) {
    const inches = parseMixedNumber(trimmed.replace(/"/g, "").trim());
    if (inches === null) {
      return null;
    }
    return inches / 12;
  }

  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    return Number.parseFloat(trimmed);
  }

  return null;
}

/**
 * Calculate the area of a polygon using the shoelace formula.
 * @param points - Array of points defining the polygon vertices
 * @returns Area in square pixels
 */
export function calculatePolygonArea(
  points: Point[],
  pageAspectRatio: number = 1
): number {
  if (points.length < 3) return 0;

  let area = 0;
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }

  return (Math.abs(area) / 2) * resolvePageAspectRatio(pageAspectRatio);
}

/**
 * Calculate the area of a polygon in square feet.
 * @param points - Array of points defining the polygon vertices (in pixels)
 * @param pixelsPerFoot - Scale factor (pixels per foot)
 * @returns Area in square feet
 */
export function calculatePolygonAreaFeet(
  points: Point[],
  pixelsPerFoot: number,
  pageAspectRatio: number = 1
): number {
  const pixelArea = calculatePolygonArea(points, pageAspectRatio);
  const feetPerPixel = 1 / pixelsPerFoot;
  return pixelArea * feetPerPixel * feetPerPixel;
}

/**
 * Zod schema for validating X'-X" format (e.g., "10'-6"").
 * Rejects 0'-0" and validates inches are 0-12.
 */
export const feetInchesSchema = z.string()
  .min(1, "Distance is required")
  .refine((val) => {
    const parsed = parseFeetInches(val);
    return parsed !== null && parsed > 0;
  }, {
    message:
      'Invalid format. Use architectural feet/inches like `10\'-6 1/2"` or a positive decimal feet value',
  });

/**
 * Parse and strictly validate a feet-inches input string.
 * @param input - Input string in format "X'-X""
 * @returns Object with feet, inches, and totalFeet, or null if validation fails
 */
export function parseFeetInchesStrict(input: string): { feet: number; inches: number; totalFeet: number } | null {
  try {
    feetInchesSchema.parse(input);

    const totalFeet = parseFeetInches(input);
    if (totalFeet === null) {
      return null;
    }

    const totalInches = totalFeet * 12;
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches - feet * 12;

    return {
      feet,
      inches,
      totalFeet,
    };
  } catch {
    return null;
  }
}

function formatDecimalMeasurement(
  value: number,
  decimals: number,
  step: number
): string {
  const rounded = roundToStep(value, step);
  return decimals === 0 ? String(Math.round(rounded)) : rounded.toFixed(decimals);
}

/**
 * Apply configured numeric precision for a measurement total.
 *
 * Architectural measurements are left unchanged because their precision is
 * handled by feet/inches parsing and formatting. Decimal measurements are
 * rounded using the configured step before they are displayed, exported, or
 * used in pricing calculations.
 *
 * Examples:
 * - `roundMeasurementValue(90.4, "SQFT")` -> `90`
 * - `roundMeasurementValue(90.6, "SQFT")` -> `91`
 *
 * @param value Measurement total as a numeric value.
 * @param measurementType Measurement unit type.
 * @returns Rounded numeric value using the configured precision rules.
 */
export function roundMeasurementValue(
  value: number,
  measurementType: MeasurementType
): number {
  const config = MEASUREMENT_FORMAT_CONFIG[measurementType];
  if (config.mode === "architectural") {
    return value;
  }

  return roundToStep(value, config.step);
}

/**
 * Format a measurement total for UI or export display.
 *
 * Linear measurements are rendered in architectural feet/inches. `SQFT` and
 * `CF` are rounded using the configured decimal precision.
 *
 * Example:
 * - `formatMeasurementValue(12.5, "LF")` -> `12'-6"`
 * - `formatMeasurementValue(90.5, "SQFT")` -> `91`
 *
 * @param value Measurement total.
 * @param measurementType Measurement unit type.
 * @returns Formatted measurement string.
 */
export function formatMeasurementValue(
  value: number,
  measurementType: MeasurementType
): string {
  const config = MEASUREMENT_FORMAT_CONFIG[measurementType];

  if (config.mode === "architectural") {
    return formatFeetInches(value, config.fractionDenominator);
  }

  return formatDecimalMeasurement(
    roundMeasurementValue(value, measurementType),
    config.decimals,
    config.step
  );
}

/**
 * Format a height input/output value using the configured height precision.
 *
 * Height values are stored in decimal feet and currently render in
 * architectural notation.
 *
 * @param value Height in decimal feet.
 * @returns Formatted height string.
 */
export function formatMeasurementHeight(value: number): string {
  const config = MEASUREMENT_FORMAT_CONFIG.height;
  return config.mode === "architectural"
    ? formatFeetInches(value, config.fractionDenominator)
    : formatDecimalMeasurement(value, config.decimals, config.step);
}

/**
 * Parse a measurement value input using the configured unit rules.
 *
 * Architectural values are parsed as decimal feet. Decimal unit types are
 * parsed numerically and rounded immediately so value edits round-trip through
 * the UI, export, and pricing paths with identical precision.
 *
 * Examples:
 * - `parseMeasurementValueInput("10'-6 1/4\"", "LF")` -> `10.520833...`
 * - `parseMeasurementValueInput("90.4", "SQFT")` -> `90`
 *
 * @param input User-provided measurement value.
 * @param measurementType Measurement unit type.
 * @returns Parsed measurement total, or `null` if invalid.
 */
export function parseMeasurementValueInput(
  input: string,
  measurementType: MeasurementType
): number | null {
  const config = MEASUREMENT_FORMAT_CONFIG[measurementType];

  if (config.mode === "architectural") {
    return parseFeetInches(input);
  }

  const normalized = input.trim().replace(/,/g, "");
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) {
    return null;
  }

  return roundMeasurementValue(Number.parseFloat(normalized), measurementType);
}

/**
 * Parse a height input into decimal feet.
 *
 * @param input User-provided height string.
 * @returns Height in decimal feet, or `null` if invalid.
 */
export function parseMeasurementHeightInput(input: string): number | null {
  return parseFeetInches(input);
}

/**
 * Calculate the total length of a multi-segment line.
 * @param vertices - Array of points defining the line vertices
 * @returns Total length in pixels
 */
export function calculateLineLength(
  vertices: Point[],
  pageAspectRatio: number = 1
): number {
  if (vertices.length < 2) return 0;
  
  let totalLength = 0;
  for (let i = 0; i < vertices.length - 1; i++) {
    totalLength += calculatePageDistance(vertices[i], vertices[i + 1], pageAspectRatio);
  }
  return totalLength;
}

/**
 * Calculate the perimeter of a polygon.
 * @param vertices - Array of points defining the polygon vertices
 * @returns Perimeter in pixels
 */
export function calculatePolygonPerimeter(
  vertices: Point[],
  pageAspectRatio: number = 1
): number {
  if (vertices.length < 2) return 0;
  if (vertices.length === 2) return calculateLineLength(vertices, pageAspectRatio);
  return (
    calculateLineLength(vertices, pageAspectRatio) +
    calculatePageDistance(vertices[vertices.length - 1], vertices[0], pageAspectRatio)
  );
}

/**
 * Check if a polygon is self-intersecting.
 * Uses the crossing number algorithm.
 * @param vertices - Array of points defining the polygon vertices
 * @returns True if the polygon self-intersects
 */
export function isSelfIntersecting(vertices: Point[]): boolean {
  if (vertices.length < 4) return false;
  
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const a1 = vertices[i];
    const a2 = vertices[(i + 1) % n];
    
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      
      const b1 = vertices[j];
      const b2 = vertices[(j + 1) % n];
      
      if (segmentsIntersect(a1, a2, b1, b2)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Check if two line segments intersect.
 * @param a1 - First endpoint of segment A
 * @param a2 - Second endpoint of segment A
 * @param b1 - First endpoint of segment B
 * @param b2 - Second endpoint of segment B
 * @returns True if segments intersect
 */
function segmentsIntersect(a1: Point, a2: Point, b1: Point, b2: Point): boolean {
  const d1 = direction(b1, b2, a1);
  const d2 = direction(b1, b2, a2);
  const d3 = direction(a1, a2, b1);
  const d4 = direction(a1, a2, b2);
  
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  
  if (d1 === 0 && onSegment(b1, b2, a1)) return true;
  if (d2 === 0 && onSegment(b1, b2, a2)) return true;
  if (d3 === 0 && onSegment(a1, a2, b1)) return true;
  if (d4 === 0 && onSegment(a1, a2, b2)) return true;
  
  return false;
}

function direction(p1: Point, p2: Point, p3: Point): number {
  return (p3.x - p1.x) * (p2.y - p1.y) - (p2.x - p1.x) * (p3.y - p1.y);
}

function onSegment(p1: Point, p2: Point, p: Point): boolean {
  return (
    p.x >= Math.min(p1.x, p2.x) && p.x <= Math.max(p1.x, p2.x) &&
    p.y >= Math.min(p1.y, p2.y) && p.y <= Math.max(p1.y, p2.y)
  );
}

/**
 * Constrain a point to the nearest horizontal or vertical axis from a reference point.
 * @param point - Point to constrain
 * @param reference - Reference point to constrain from
 * @returns Constrained point
 */
export function constrainToAxis(point: Point, reference: Point): Point {
  const dx = point.x - reference.x;
  const dy = point.y - reference.y;
  
  if (Math.abs(dx) > Math.abs(dy)) {
    return { x: point.x, y: reference.y };
  } else {
    return { x: reference.x, y: point.y };
  }
}

/**
 * Constrain a point to form a square (equal width and height) from a reference point.
 * Uses the larger delta as the square side length.
 * @param point - Point to constrain
 * @param reference - Reference point to constrain from
 * @returns Constrained point forming a square
 */
export function constrainToSquare(point: Point, reference: Point): Point {
  const dx = point.x - reference.x;
  const dy = point.y - reference.y;
  const delta = Math.max(Math.abs(dx), Math.abs(dy));
  const signX = dx >= 0 ? 1 : -1;
  const signY = dy >= 0 ? 1 : -1;
  return {
    x: reference.x + signX * delta,
    y: reference.y + signY * delta,
  };
}

/**
 * Constrain a point to the nearest 45-degree angle from a reference point.
 * @param point - Point to constrain
 * @param reference - Reference point to constrain from
 * @param pageAspectRatio - Page height / width ratio
 * @returns Constrained point at 45-degree increment
 */
export function constrainToAngle(
  point: Point,
  reference: Point,
  pageAspectRatio: number = 1
): Point {
  const aspectRatio = resolvePageAspectRatio(pageAspectRatio);
  const dx = point.x - reference.x;
  const scaledDy = (point.y - reference.y) * aspectRatio;
  const distance = Math.sqrt(dx * dx + scaledDy * scaledDy);

  const angle = Math.atan2(scaledDy, dx);
  const snapAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);

  return {
    x: reference.x + distance * Math.cos(snapAngle),
    y: reference.y + (distance * Math.sin(snapAngle)) / aspectRatio,
  };
}

/**
 * Quantize a real-world distance (feet) to the nearest increment.
 * @param distanceFeet - Distance in feet
 * @param incrementFeet - Snap increment in feet
 * @returns Snapped distance in feet
 */
export function snapDistanceToIncrement(
  distanceFeet: number,
  incrementFeet: number
): number {
  if (!Number.isFinite(distanceFeet)) {
    return 0;
  }

  if (!Number.isFinite(incrementFeet) || incrementFeet <= 0) {
    return distanceFeet;
  }

  return Math.round(distanceFeet / incrementFeet) * incrementFeet;
}

/**
 * Snap a point to the nearest distance increment from an anchor while preserving direction.
 * @param anchor - Origin point for distance snapping
 * @param point - Candidate point to snap
 * @param pixelsPerFoot - Scale factor (pixels per foot)
 * @param incrementFeet - Snap increment in feet
 * @returns Snapped point in page coordinates
 */
export function snapPointToIncrement(
  anchor: Point,
  point: Point,
  pixelsPerFoot: number,
  incrementFeet: number,
  pageAspectRatio: number = 1
): Point {
  if (!Number.isFinite(pixelsPerFoot) || pixelsPerFoot <= 0) {
    return point;
  }

  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  const distancePixels = calculatePageDistance(anchor, point, pageAspectRatio);
  if (distancePixels === 0) {
    return point;
  }

  const distanceFeet = pixelsToFeet(distancePixels, pixelsPerFoot);
  const snappedDistanceFeet = snapDistanceToIncrement(distanceFeet, incrementFeet);
  const snappedDistancePixels = feetToPixels(snappedDistanceFeet, pixelsPerFoot);
  const scale = snappedDistancePixels / distancePixels;

  return {
    x: anchor.x + dx * scale,
    y: anchor.y + dy * scale,
  };
}

/**
 * Snap a rectangle corner point to the nearest increment from an anchor corner.
 * @param args.anchor - Fixed rectangle corner
 * @param args.point - Dragged rectangle corner
 * @param args.pixelsPerFoot - Scale factor (pixels per foot)
 * @param args.incrementFeet - Snap increment in feet
 * @param args.lockSquare - Whether both sides should share one snapped side length
 * @returns Snapped dragged corner point
 */
export function snapRectanglePointToIncrement(args: {
  anchor: Point;
  point: Point;
  pixelsPerFoot: number;
  incrementFeet: number;
  lockSquare?: boolean;
  pageAspectRatio?: number;
}): Point {
  const {
    anchor,
    point,
    pixelsPerFoot,
    incrementFeet,
    lockSquare = false,
    pageAspectRatio = 1,
  } = args;
  if (!Number.isFinite(pixelsPerFoot) || pixelsPerFoot <= 0) {
    return point;
  }

  const aspectRatio = resolvePageAspectRatio(pageAspectRatio);
  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  const signX = dx >= 0 ? 1 : -1;
  const signY = dy >= 0 ? 1 : -1;

  if (lockSquare) {
    const rawSidePixels = Math.max(Math.abs(dx), Math.abs(dy) * aspectRatio);
    const rawSideFeet = pixelsToFeet(rawSidePixels, pixelsPerFoot);
    const snappedSideFeet = snapDistanceToIncrement(rawSideFeet, incrementFeet);
    const snappedSidePixels = feetToPixels(snappedSideFeet, pixelsPerFoot);
    return {
      x: anchor.x + signX * snappedSidePixels,
      y: anchor.y + signY * (snappedSidePixels / aspectRatio),
    };
  }

  const rawWidthFeet = pixelsToFeet(Math.abs(dx), pixelsPerFoot);
  const rawHeightFeet = pixelsToFeet(Math.abs(dy) * aspectRatio, pixelsPerFoot);
  const snappedWidthPixels = feetToPixels(
    snapDistanceToIncrement(rawWidthFeet, incrementFeet),
    pixelsPerFoot
  );
  const snappedHeightPixels =
    feetToPixels(
      snapDistanceToIncrement(rawHeightFeet, incrementFeet),
      pixelsPerFoot
    ) / aspectRatio;
  return {
    x: anchor.x + signX * snappedWidthPixels,
    y: anchor.y + signY * snappedHeightPixels,
  };
}



/**
 * Minimum measurement size in pixels.
 */
const MIN_MEASUREMENT_PIXELS = 5;

/**
 * Maximum polygon vertices.
 */
const MAX_POLYGON_VERTICES = 50;

/**
 * Check if a line measurement is valid.
 * @param vertices - Line vertices
 * @returns True if valid
 */
export function isValidLine(vertices: Point[]): boolean {
  if (vertices.length < 2) return false;
  return calculateLineLength(vertices) >= MIN_MEASUREMENT_PIXELS;
}

/**
 * Check if a polygon is valid.
 * @param vertices - Polygon vertices
 * @param checkSelfIntersection - Whether to check for self-intersection
 * @returns Object with isValid and error message if invalid
 */
export function isValidPolygon(
  vertices: Point[],
  checkSelfIntersection: boolean = true
): { isValid: boolean; error?: string } {
  if (vertices.length < 3) {
    return { isValid: false, error: "Polygon must have at least 3 vertices" };
  }
  
  if (vertices.length > MAX_POLYGON_VERTICES) {
    return { isValid: false, error: `Polygon cannot have more than ${MAX_POLYGON_VERTICES} vertices` };
  }
  
  const area = calculatePolygonArea(vertices);
  if (area < MIN_MEASUREMENT_PIXELS * MIN_MEASUREMENT_PIXELS) {
    return { isValid: false, error: "Polygon is too small" };
  }
  
  if (checkSelfIntersection && isSelfIntersecting(vertices)) {
    return { isValid: false, error: "Polygon cannot self-intersect" };
  }
  
  return { isValid: true };
}

/**
 * Compute measurement value based on tool type and geometry.
 * @param tool - Tool type
 * @param measurementType - LF, SQFT, or CF
 * @param geometry - Geometry data
 * @param height - Height for CF calculations
 * @param pixelsPerFoot - Scale factor
 * @returns Computed value in appropriate units
 */
export function computeMeasurementValue(
  tool: MeasurementTool,
  measurementType: MeasurementType,
  geometry: MeasurementGeometry,
  height: number | undefined,
  pixelsPerFoot: number,
  pageAspectRatio: number = 1
): number {
  return recomputeMeasurementValue({ tool, measurementType, geometry, height }, pixelsPerFoot, pageAspectRatio);
}


function getGeometryAnchor(geometry: MeasurementGeometry): Point {
  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    return {
      x: geometry.x + geometry.width / 2,
      y: geometry.y + geometry.height / 2,
    };
  }

  const pointCount = geometry.vertices.length || 1;
  const totals = geometry.vertices.reduce(
    (acc, vertex) => ({
      x: acc.x + vertex.x,
      y: acc.y + vertex.y,
    }),
    { x: 0, y: 0 }
  );

  return {
    x: totals.x / pointCount,
    y: totals.y / pointCount,
  };
}

/**
 * Uniformly scale measurement geometry around its center anchor.
 *
 * Rectangles scale from their center point. Polygon and line vertices scale
 * from the centroid of the current vertices. The function is pure and returns a
 * new geometry object.
 *
 * Example:
 * - A `120x60` rectangle scaled by `2` becomes `240x120` with the same center.
 *
 * @param geometry Geometry in page-space units.
 * @param scaleFactor Uniform scale factor to apply.
 * @returns New scaled geometry.
 */
export function scaleMeasurementGeometry(
  geometry: MeasurementGeometry,
  scaleFactor: number
): MeasurementGeometry {
  if (!Number.isFinite(scaleFactor) || scaleFactor <= 0) {
    throw new Error("Scale factor must be positive");
  }

  const anchor = getGeometryAnchor(geometry);

  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    const nextWidth = geometry.width * scaleFactor;
    const nextHeight = geometry.height * scaleFactor;
    return {
      type: MEASUREMENT_GEOMETRY_TYPES.rect,
      x: anchor.x - nextWidth / 2,
      y: anchor.y - nextHeight / 2,
      width: nextWidth,
      height: nextHeight,
    };
  }

  return mapPolygonPoints(geometry, vertex => ({
    x: anchor.x + (vertex.x - anchor.x) * scaleFactor,
    y: anchor.y + (vertex.y - anchor.y) * scaleFactor,
  }));
}

/**
 * Rescale geometry so a measurement value matches a new target.
 *
 * Scaling rules:
 * - `LF` scales linearly by `target / current`
 * - Area/volume-based shapes (`SQFT`/`CF` on rectangles and polygons) scale by
 *   `sqrt(target / current)` to preserve aspect ratio
 * - Line tools always scale linearly because their geometry is one-dimensional
 *
 * The returned geometry stays centered on the existing anchor/centroid.
 *
 * Example:
 * - Rescaling a rectangle from `72 SQFT` to `288 SQFT` doubles width and height.
 *
 * @param args.tool Measurement tool type.
 * @param args.measurementType Measurement unit type.
 * @param args.geometry Existing page-space geometry.
 * @param args.currentValue Current computed measurement value.
 * @param args.targetValue Desired measurement value.
 * @returns Rescaled geometry.
 */
export function rescaleMeasurementGeometry(args: {
  tool: MeasurementTool;
  measurementType: MeasurementType;
  geometry: MeasurementGeometry;
  currentValue: number;
  targetValue: number;
}): MeasurementGeometry {
  if (args.currentValue <= 0 || args.targetValue <= 0) {
    throw new Error("Measurement values must be positive");
  }

  const isUniformLengthScale =
    args.measurementType === MEASUREMENT_TYPES.LF ||
    args.tool === MEASUREMENT_TOOL_IDS.line ||
    args.tool === MEASUREMENT_TOOL_IDS.polyline;
  const scaleFactor = isUniformLengthScale
    ? args.targetValue / args.currentValue
    : Math.sqrt(args.targetValue / args.currentValue);

  return scaleMeasurementGeometry(args.geometry, scaleFactor);
}
